import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTime, maskIban } from '@/utils/format';
import Modal from './Modal';
import { RequestStatusBadge, CodeStatusBadge } from './StatusBadge';
import type { AdminRequest } from '@/mocks/adminRequests';

type RequestDetailModalProps = {
  request: AdminRequest;
  onClose: () => void;
};

export default function RequestDetailModal({ request, onClose }: RequestDetailModalProps) {
  const { t } = useTranslation();

  const rows: { label: string; value: string }[] = [
    { label: t('admin.detail.reference'), value: request.reference },
    { label: t('admin.detail.client'), value: `${request.clientName} (${request.clientCode})` },
    { label: t('admin.detail.beneficiary'), value: request.fullName },
    { label: t('admin.detail.amount'), value: formatCurrency(request.amount, request.currency) },
    { label: t('admin.detail.bank'), value: request.bankName },
    { label: t('admin.detail.iban'), value: maskIban(request.iban) },
    { label: t('admin.detail.phone'), value: request.phone },
    { label: t('admin.detail.email'), value: request.email },
    { label: t('admin.detail.submittedAt'), value: formatDateTime(request.createdAt) },
    { label: t('admin.detail.codeGeneratedAt'), value: formatDateTime(request.codeGeneratedAt) },
    { label: t('admin.detail.codeExpiresAt'), value: formatDateTime(request.codeExpiresAt) },
  ];

  if (request.codeUsedAt) {
    rows.push({ label: t('admin.detail.codeUsedAt'), value: formatDateTime(request.codeUsedAt) });
  }
  if (request.activatedAt) {
    rows.push({ label: t('admin.detail.activatedAt'), value: formatDateTime(request.activatedAt) });
  }
  if (request.activatedBy) {
    rows.push({ label: t('admin.detail.activatedBy'), value: request.activatedBy });
  }
  rows.push({ label: t('admin.detail.processingEstimate'), value: request.processingEstimate });

  return (
    <Modal title={t('admin.detail.title')} subtitle={request.reference} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <RequestStatusBadge status={request.status} />
          <CodeStatusBadge status={request.codeStatus} />
        </div>

        <div className="divide-y divide-background-200 rounded-md border border-background-200 bg-background-50">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-4 px-4 py-3">
              <span className="text-xs text-foreground-500">{row.label}</span>
              <span className="text-right text-sm font-semibold text-foreground-900">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}